/**
 * ItemDetailModal - Unified modal for products and packages
 * Phase C3: Product Detail Modal
 *
 * Usage:
 * <ItemDetailModal
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 *   itemType="product" // or "package"
 *   itemId={productId}
 * />
 */

import React, { useState, useEffect } from "react";
import { useCart } from "../hooks/useCart";
import { getPackageIcon, getPackageThumbnail } from "../utils/packageIcons";
import PackagePurchaseModal from "./PackagePurchaseModal";
import "./ItemDetailModal.css";

const formatVariantLabel = (variant) => {
  if (!variant || typeof variant !== "object") return "Default";

  if (variant.variantLabel && String(variant.variantLabel).trim()) {
    return String(variant.variantLabel).trim();
  }

  if (Array.isArray(variant.variantAttributes) && variant.variantAttributes.length > 0) {
    const parts = variant.variantAttributes
      .map((attr) => {
        const name = String(attr?.dimensionName || attr?.dimensionKey || "").trim();
        const value = String(attr?.label || attr?.value || "").trim();
        if (!name && !value) return "";
        if (!name) return value;
        if (!value) return name;
        return `${name}: ${value}`;
      })
      .filter(Boolean);

    if (parts.length > 0) return parts.join(" • ");
  }

  return String(variant.variantKey || "Default").trim() || "Default";
};

const getVariantSortSignature = (variant) => {
  if (!variant || typeof variant !== "object") return "";

  if (Array.isArray(variant.variantAttributes) && variant.variantAttributes.length > 0) {
    return variant.variantAttributes
      .slice()
      .sort((left, right) => {
        const leftPos = Number.isFinite(left?.position) ? left.position : Number.MAX_SAFE_INTEGER;
        const rightPos = Number.isFinite(right?.position) ? right.position : Number.MAX_SAFE_INTEGER;
        return leftPos - rightPos;
      })
      .map((attr) => String(attr?.label || attr?.value || "").trim().toLowerCase())
      .filter(Boolean)
      .join("|");
  }

  return String(variant.variantLabel || variant.variantKey || "")
    .trim()
    .toLowerCase();
};

const sortVariantsAscending = (variants = []) => {
  return [...variants].sort((left, right) => {
    const leftSignature = getVariantSortSignature(left);
    const rightSignature = getVariantSortSignature(right);

    const signatureCompare = leftSignature.localeCompare(rightSignature, undefined, {
      numeric: true,
      sensitivity: "base",
    });

    if (signatureCompare !== 0) return signatureCompare;

    const leftLabel = formatVariantLabel(left);
    const rightLabel = formatVariantLabel(right);
    return leftLabel.localeCompare(rightLabel, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
};

const ItemDetailModal = ({
  isOpen,
  onClose,
  itemType = "product",
  itemId,
  ownerType = "shop",
  ownerId = null,
}) => {
  const { addToCart, loading: cartLoading } = useCart();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Package-specific state
  const [isGift, setIsGift] = useState(false);
  const [useImmediately, setUseImmediately] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [giftMessage, setGiftMessage] = useState("");

  const [addSuccess, setAddSuccess] = useState(false);

  // Packages are never added to the cart - they open their own purchase flow
  const [isPackagePurchaseOpen, setIsPackagePurchaseOpen] = useState(false);

  // Fetch item details
  useEffect(() => {
    if (!isOpen || !itemId) return;

    const fetchItem = async () => {
      try {
        setLoading(true);
        setError(null);

        let endpoint;
        if (itemType === "product") {
          // For professional products, we need to fetch from the staff product list
          // and find the specific product. For shop products, use the shop-product endpoint.
          if (ownerType === "professional" && ownerId) {
            // Fetch all professional products and find the one with matching ID
            endpoint = `/api/staff/${ownerId}/products/public`;
          } else {
            endpoint = `/api/v2/products/shop-product/${itemId}`;
          }
        } else {
          endpoint = `/api/packages/${itemId}`;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com"}${endpoint}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch ${itemType}`);
        }

        const data = await response.json();

        // For professional products, find the specific product from the list
        if (itemType === "product" && ownerType === "professional") {
          const product = data.products?.find((p) => p._id === itemId);
          if (!product) {
            throw new Error("Product not found");
          }
          setItem(product);
          const firstVariant = Array.isArray(product.variants)
            ? product.variants.find((variant) => variant?._id)
            : null;
          setSelectedVariantId(firstVariant?._id ? String(firstVariant._id) : "");
        } else {
          const resolvedItem = itemType === "product" ? data.product : data.package;
          setItem(resolvedItem);
          if (itemType === "product") {
            const firstVariant = Array.isArray(resolvedItem?.variants)
              ? resolvedItem.variants.find((variant) => variant?._id)
              : null;
            setSelectedVariantId(firstVariant?._id ? String(firstVariant._id) : "");
          }
        }
        setSelectedImage(0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [isOpen, itemId, itemType, ownerType, ownerId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuantity(1);
      setIsGift(false);
      setUseImmediately(false);
      setRecipientEmail("");
      setRecipientPhone("");
      setGiftMessage("");
      setAddSuccess(false);
      setIsPackagePurchaseOpen(false);
      setError(null);
      setSelectedVariantId("");
    }
  }, [isOpen]);

  const productVariants =
    itemType === "product" && Array.isArray(item?.variants)
      ? item.variants.filter((variant) => variant && variant._id)
      : [];

  const sortedProductVariants = sortVariantsAscending(productVariants);

  useEffect(() => {
    if (!isOpen || itemType !== "product" || sortedProductVariants.length === 0) {
      return;
    }

    const hasSelected = sortedProductVariants.some(
      (variant) => String(variant._id) === String(selectedVariantId),
    );

    if (!hasSelected) {
      setSelectedVariantId(String(sortedProductVariants[0]._id));
    }
  }, [isOpen, itemType, selectedVariantId, sortedProductVariants]);

  const selectedVariant =
    sortedProductVariants.find(
      (variant) => String(variant._id) === String(selectedVariantId),
    ) || sortedProductVariants[0] || null;

  const selectedProductId =
    itemType === "product" && selectedVariant?._id
      ? selectedVariant._id
      : itemId;

  const currentPrice =
    itemType === "product"
      ? typeof selectedVariant?.price === "number"
        ? selectedVariant.price
        : typeof item?.price === "number"
          ? item.price
          : 0
      : item?.packagePrice;

  const currentStock =
    itemType === "product"
      ? typeof selectedVariant?.stock === "number"
        ? selectedVariant.stock
        : item?.stock
      : undefined;

  const handleAddToCart = async () => {
    try {
      setError(null);

      if (itemType === "product") {
        // Validate stock
        if (currentStock !== undefined && currentStock < quantity) {
          setError(`Only ${currentStock} available in stock`);
          return;
        }

        const result = await addToCart(selectedProductId, quantity);

        if (result.success) {
          setAddSuccess(true);
          setTimeout(() => {
            onClose();
          }, 1500);
        } else {
          setError(result.error || "Failed to add to cart");
        }
      } else {
        // Package: never add to the cart. A package is bought through its own
        // purchase flow (PackagePurchaseModal), which this modal opens below.
        setIsPackagePurchaseOpen(true);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="item-modal-overlay" onClick={onClose}>
      <div className="item-modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="item-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {loading && (
          <div className="item-modal-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        )}

        {error && !loading && (
          <div className="item-modal-error">
            <p>{error}</p>
            <button onClick={onClose}>Close</button>
          </div>
        )}

        {item && !loading && (
          <div className="item-modal-body">
            {/* Image Section */}
            <div className="item-modal-images">
              <div className="item-modal-main-image">
                <img
                  src={
                    itemType === "package"
                      ? getPackageIcon(item)
                      : item.images?.[selectedImage] ||
                        "https://placehold.co/400x400/9ca3af/ffffff/png?text=No+Image"
                  }
                  alt={item.name}
                />
              </div>
              {itemType === "product" && item.images?.length > 1 && (
                <div className="item-modal-thumbnails">
                  {item.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${item.name} ${index + 1}`}
                      className={selectedImage === index ? "active" : ""}
                      onClick={() => setSelectedImage(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="item-modal-details">
              <h2>{item.name}</h2>

              {itemType === "product" && item.brand && (
                <p className="item-brand">{item.brand}</p>
              )}

              <div className="item-price">
                $
                {typeof (itemType === "package"
                  ? item.packagePrice
                  : currentPrice) === "number"
                  ? (itemType === "package"
                      ? item.packagePrice
                      : currentPrice
                    ).toFixed(2)
                  : "0.00"}
              </div>

              {itemType === "product" && currentStock !== undefined && (
                <p className="item-stock">
                  {currentStock > 0 ? (
                    `${currentStock} in stock`
                  ) : (
                    <span className="out-of-stock">Out of stock</span>
                  )}
                </p>
              )}

              {itemType === "product" && sortedProductVariants.length > 1 && (
                <div className="item-variants">
                  <h3>Options</h3>
                  <div className="item-variant-list">
                    {sortedProductVariants.map((variant) => {
                      const label = formatVariantLabel(variant);
                      const isSelected =
                        String(selectedVariant?._id) === String(variant._id);

                      return (
                        <button
                          key={variant._id}
                          type="button"
                          className={`item-variant-chip${isSelected ? " active" : ""}`}
                          onClick={() => {
                            setSelectedVariantId(String(variant._id));
                            setQuantity(1);
                          }}
                        >
                          <span className="item-variant-chip-label">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {itemType === "package" && (
                <div className="package-info">
                  <p className="package-sessions">
                    <strong>
                      {item.totalSessions?.value || item.totalSessions}
                    </strong>{" "}
                    sessions included
                  </p>
                  <p className="package-validity">
                    Valid for{" "}
                    <strong>
                      {item.validityPeriod?.value || item.validityPeriod}{" "}
                      {item.validityPeriod?.unit || "days"}
                    </strong>
                  </p>
                  {item.servicesIncluded?.length > 0 && (
                    <div className="package-services">
                      <p>
                        <strong>Services included:</strong>
                      </p>
                      <ul>
                        {item.servicesIncluded.map((service, idx) => (
                          <li key={idx}>
                            {service.serviceName || service.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {item.description && (
                <div className="item-description">
                  <h3>Description</h3>
                  <p>{item.description}</p>
                </div>
              )}

              {/* Product: Quantity Selector */}
              {itemType === "product" && (
                <div className="item-quantity">
                  <label>Quantity:</label>
                  <div className="quantity-selector">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                      }
                      min="1"
                      max={currentStock}
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      disabled={
                        currentStock !== undefined && quantity >= currentStock
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {/* Package: Usage Options */}
              {itemType === "package" && (
                <div className="package-options">
                  <div className="package-option-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={useImmediately}
                        onChange={(e) => setUseImmediately(e.target.checked)}
                      />
                      <span>Use first session now (book after purchase)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && <div className="item-modal-inline-error">{error}</div>}

              {/* Success Message */}
              {addSuccess && (
                <div className="item-modal-success">✓ Added to cart!</div>
              )}

              {/* Add to Cart Button */}
              <button
                className="item-add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={
                  cartLoading ||
                  addSuccess ||
                  (itemType === "product" && currentStock === 0) ||
                  (itemType === "package" &&
                    isGift &&
                    !recipientEmail &&
                    !recipientPhone)
                }
              >
                {cartLoading
                  ? "Adding..."
                  : addSuccess
                    ? "Added to Cart!"
                    : "Add to Cart"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Packages never enter the cart - they use their own purchase flow */}
      {itemType === "package" && isPackagePurchaseOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <PackagePurchaseModal
            isOpen={isPackagePurchaseOpen}
            onClose={() => setIsPackagePurchaseOpen(false)}
            package={item}
            shopId={item?.availableAtShops?.[0] || item?.shopId}
          />
        </div>
      )}
    </div>
  );
};

export default ItemDetailModal;
